<template>
  <div>
    <!-- Section title — plain lowercase heading like other sections -->
    <div class="flex flex-col items-center mb-10">
      <h3 class="heading-2 font-title font-bold text-flax-smoke-200 text-center mb-2">
        peek into my life •ᴗ•
      </h3>
      <div class="flex items-center gap-4 mt-1">
        <img :src="s4" alt="" class="w-10 h-auto pointer-events-none select-none" />
        <p class="hidden sm:block text-xs text-flax-smoke-300/50 font-fancy" style="font-size: 18px;">hover for captions</p>
        <img :src="s2" alt="" class="w-12 h-auto pointer-events-none select-none" />
      </div>
    </div>

    <!-- ── Mobile carousel (hidden on sm+) ─────────────────────────── -->
    <div class="block sm:hidden relative px-2 pb-16">
      <img :src="s1" alt="" class="absolute -top-6 left-0 w-14 pointer-events-none select-none z-10" style="transform: rotate(-8deg)" />
      <img :src="s5" alt="" class="absolute -top-4 right-0 w-10 pointer-events-none select-none z-10" style="transform: rotate(8deg)" />

      <!-- Carousel container -->
      <div class="relative overflow-hidden rounded-lg border border-flax-smoke-600" style="height: 60vw">
        <!-- Sliding track -->
        <div
          class="flex h-full transition-transform duration-300 ease-in-out"
          :style="{ transform: `translateX(-${mobileSlide * 100}%)` }"
        >
          <div
            v-for="(item, i) in items"
            :key="'m' + i"
            class="relative flex-none w-full h-full cursor-pointer"
            @click.stop="nextSlide"
            @touchend.stop.prevent="nextSlide"
          >
            <video
              v-if="item.type === 'video'"
              ref="mobileVideoRefs"
              :src="item.src"
              class="w-full h-full object-cover"
              preload="metadata"
              playsinline
              loop
            />
            <img
              v-else
              :src="item.src"
              :alt="item.caption"
              class="w-full h-full object-cover"
              :style="item.objectPosition ? { objectPosition: item.objectPosition } : {}"
            />
            <div
              v-if="item.type === 'video' && !mobilePlaying"
              class="absolute inset-0 flex flex-col items-center justify-center bg-black/60 text-white space-y-3 z-10"
            >
              <p class="text-sm font-semibold px-4">🔊 Headphones recommended</p>
              <button @click.stop="playMobileVideo(i)" @touchend.stop.prevent="playMobileVideo(i)" class="border border-white px-4 py-2 rounded-full text-white text-sm font-semibold">Play</button>
            </div>
          </div>
        </div>

        <!-- Navigation buttons -->
        <button
          @click.stop="prevSlide"
          @touchend.stop.prevent="prevSlide"
          class="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          @click.stop="nextSlide"
          @touchend.stop.prevent="nextSlide"
          class="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <!-- Caption below carousel — tap to advance -->
      <p
        class="font-fancy text-flax-smoke-300 text-sm leading-snug mt-3 text-center px-2 cursor-pointer"
        @click.stop="nextSlide"
        @touchend.stop.prevent="nextSlide"
      >{{ items[mobileSlide].caption }}</p>

      <!-- Carousel dots -->
      <div class="flex justify-center gap-3 mt-5">
        <div
          v-for="(_, i) in items"
          :key="'d' + i"
          @click.stop="goToSlide(i)"
          @touchend.stop.prevent="goToSlide(i)"
          class="w-4 h-4 rounded-full cursor-pointer transition-colors duration-300"
          :class="mobileSlide === i ? 'bg-flax-smoke-300' : 'bg-flax-smoke-600'"
        />
      </div>

      <img :src="s9" alt="" class="absolute bottom-16 left-0 w-10 pointer-events-none select-none z-10" style="transform: rotate(5deg)" />
      <img :src="s10" alt="" class="absolute bottom-16 right-0 w-10 pointer-events-none select-none z-10" style="transform: rotate(10deg)" />
    </div>

    <!-- Wrapper with padding so stickers hanging off edges aren't clipped — desktop only -->
    <div class="collage-wrapper hidden sm:block">

      <!-- Y2K Chrome Stickers — mix of inside and outside, hidden on mobile -->
      <img
        v-for="(s, i) in stickers"
        :key="'s' + i"
        :src="s.src"
        alt=""
        class="absolute pointer-events-none select-none hidden sm:block"
        :style="{
          top:    s.style.top    ?? 'auto',
          left:   s.style.left  ?? 'auto',
          right:  s.style.right ?? 'auto',
          bottom: s.style.bottom ?? 'auto',
          width:  s.style.width,
          transform: `rotate(${s.style.rotate})`,
          zIndex: s.style.zIndex,
        }"
      />

      <div class="collage-grid">
        <div
          v-for="(item, i) in items"
          :key="i"
          class="collage-item group relative cursor-pointer"
          :style="{ gridColumn: item.gridColumn, gridRow: item.gridRow }"
          @click="handleClick(i)"
        >
          <video
            v-if="item.type === 'video'"
            ref="videoRefs"
            :src="item.src"
            class="collage-media"
            :style="item.objectPosition ? { objectPosition: item.objectPosition } : {}"
            loop muted playsinline preload="metadata"
          />
          <img
            v-else
            :src="item.src"
            :alt="item.caption"
            class="collage-media"
            :style="item.objectPosition ? { objectPosition: item.objectPosition } : {}"
          />

          <!-- Caption: slides up from bottom, always above stickers -->
          <div
            class="caption-label absolute bottom-0 left-0 right-0 px-4 py-3
                   opacity-0 translate-y-1
                   group-hover:opacity-100 group-hover:translate-y-0
                   transition-all duration-300 pointer-events-none"
            :style="{ backgroundColor: item.labelColor }"
          >
            <p class="font-fancy text-flax-smoke-950 text-lg leading-snug">
              {{ item.caption }}
            </p>
          </div>

          <!-- Video play button with volume warning -->
          <div
            v-if="item.type === 'video' && !isPlaying[i]"
            class="absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-300 bg-black/40 rounded-lg"
          >
            <p class="text-xs font-semibold text-flax-smoke-100 mb-3">🔊 Headphones recommended</p>
            <div class="flex items-center justify-center size-12 rounded-full bg-black/50 backdrop-blur-sm">
              <svg class="ml-0.5 size-5 text-flax-smoke-100" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue';
import {
  hobbiesDance,
  hobbiesSkate,
  hobbiesTheater,
  hobbiesTalk,
  hobbiesHack,
  guitarVideo,
} from '@/assets/images';

import s1  from '@/assets/stickers_asset/sticker1.png';
import s2  from '@/assets/stickers_asset/sticker2.png';
import s4  from '@/assets/stickers_asset/4.png';
import s5  from '@/assets/stickers_asset/5.png';
import s6  from '@/assets/stickers_asset/6.png';
import s7  from '@/assets/stickers_asset/7.png';
import s8  from '@/assets/stickers_asset/8.png';
import s9  from '@/assets/stickers_asset/9.png';
import s10 from '@/assets/stickers_asset/10.png';

// ─── Sticker positions ────────────────────────────────────────────────────────
// "half outer half inner" = sticker center sits at the edge/corner of the collage
// or at the intersection of 2–4 photos inside the grid.
interface StickerStyle {
  top?: string; left?: string; right?: string; bottom?: string;
  width: string; rotate: string; zIndex: number;
}
const stickers: { src: string; style: StickerStyle }[] = [
  // 8-wing star — TOP-LEFT: overlaps bioinf photo top-left corner
  { src: s1,  style: { top: '-5%',    left: '2%',    width: '12%', rotate: '-5deg',  zIndex: 20 } },
  // Headphones — TOP-RIGHT: overlaps dance photo top-right corner
  { src: s5,  style: { top: '-0.7%',    right: '1%',   width: '8%',  rotate: '6deg',   zIndex: 20 } },
  // Atom — outer RIGHT edge at row seam (moved from center to right outer corner)
  { src: s6,  style: { top: '44%',    right: '-2%',  width: '10%', rotate: '-3deg',  zIndex: 20 } },
  // Star — row seam, slightly left of before
  { src: s7,  style: { top: '46%',    left: '64.5%',   width: '7%',  rotate: '4deg',   zIndex: 20 } },
  // Ribbon/bow — pulled slightly down so it doesn't cover bioinf photo title area
  { src: s8,  style: { top: '45.6%',    left: '16%',   width: '10%', rotate: '-5deg',  zIndex: 20 } },
  // Moon — overlaps theater photo's bottom-left corner
  { src: s9,  style: { bottom: '2%',  left: '-1%',   width: '8%',  rotate: '3deg',   zIndex: 20 } },
  // Rose — bottom-right corner of ht6 photo, just outside the edge, tilted right
  { src: s10, style: { bottom: '2%',  right: '-1%',  width: '8%',  rotate: '12deg',  zIndex: 20 } },
];

// ─── Gallery items ─────────────────────────────────────────────────────────
interface GalleryItem {
  src: string; type: 'image' | 'video';
  caption: string; gridColumn: string; gridRow: string; labelColor: string;
  objectPosition?: string;
}

const items: GalleryItem[] = [
  // Mobile carousel order: hack → skate → guitar → talk → dance → theater
  // Desktop: each item's gridColumn/gridRow controls placement regardless of array order
  {
    src: hobbiesHack, type: 'image',
    caption: '[builder] at Hack the North 2026: our team built orca-ctl - an embedded system that autonomously recovers servers during a data center outage.',
    gridColumn: '1 / 3', gridRow: '1 / 2',
    labelColor: 'rgba(196,168,130,0.92)',
  },
  {
    src: hobbiesSkate, type: 'image',
    caption: '[*very amateur* skater] shoutout to Queer Skate Toronto for building safe, inclusive spaces for the LGBTQIA+ community!',
    gridColumn: '2 / 4', gridRow: '2 / 3',
    labelColor: 'rgba(196,168,130,0.92)',
    objectPosition: 'center 20%',
  },
  {
    src: guitarVideo, type: 'video',
    caption: '[guitar & bass] from playing live gigs in Vietnam to building a digital community of 13K+ music enthusiasts and generating 2M+ views through my guitar content.',
    gridColumn: '3 / 4', gridRow: '1 / 2',
    labelColor: 'rgba(143,168,142,0.92)',
  },
  {
    src: hobbiesTalk, type: 'image',
    caption: '[host] GDG York\'s first Googler panel @ Google Toronto.',
    gridColumn: '4 / 5', gridRow: '2 / 3',
    labelColor: 'rgba(143,168,142,0.92)',
  },
  {
    src: hobbiesDance, type: 'image',
    caption: '[breaking & hip-hop] performing with Dance Collective at Vanier College Productions.',
    gridColumn: '4 / 5', gridRow: '1 / 2',
    labelColor: 'rgba(196,137,138,0.92)',
  },
  {
    src: hobbiesTheater, type: 'image',
    caption: '[theater] from performing to directing high school musical productions.',
    gridColumn: '1 / 2', gridRow: '2 / 3',
    labelColor: 'rgba(232,232,223,0.92)',
  },
];

const isPlaying = reactive<Record<number, boolean>>({});
const videoRefs = ref<HTMLVideoElement[]>([]);

// ── Mobile carousel ──────────────────────────────────────────────────────────
const mobileSlide = ref(0);
const mobilePlaying = ref(false);
const mobileVideoRefs = ref<HTMLVideoElement[]>([]);

function nextSlide() {
  mobileVideoRefs.value.forEach(v => { v.pause(); v.currentTime = 0; });
  mobileSlide.value = (mobileSlide.value + 1) % items.length;
  mobilePlaying.value = false;
}
function prevSlide() {
  mobileVideoRefs.value.forEach(v => { v.pause(); v.currentTime = 0; });
  mobileSlide.value = (mobileSlide.value - 1 + items.length) % items.length;
  mobilePlaying.value = false;
}
function goToSlide(i: number) {
  mobileVideoRefs.value.forEach(v => { v.pause(); v.currentTime = 0; });
  mobileSlide.value = i;
  mobilePlaying.value = false;
}
function playMobileVideo(index: number) {
  const videoItems = items.map((item, i) => ({ item, i })).filter(({ item }) => item.type === 'video');
  const videoIdx = videoItems.findIndex(({ i }) => i === index);
  const el = mobileVideoRefs.value[videoIdx];
  if (el) { el.muted = false; el.play(); mobilePlaying.value = true; }
}

function handleClick(index: number) {
  const item = items[index];
  if (item.type !== 'video') return;
  const videoEl = findVideoEl(index);
  if (!videoEl) return;
  if (videoEl.paused) {
    videoEl.muted = false;
    videoEl.play();
    isPlaying[index] = true;
  } else {
    videoEl.pause();
    isPlaying[index] = false;
  }
}

function findVideoEl(itemIndex: number): HTMLVideoElement | null {
  const videoItems = items
    .map((item, i) => ({ item, i }))
    .filter(({ item }) => item.type === 'video');
  const videoIdx = videoItems.findIndex(({ i }) => i === itemIndex);
  return videoRefs.value[videoIdx] ?? null;
}
</script>

<style scoped>
/* Outer padding = breathing room for edge stickers; overflow visible so they show */
.collage-wrapper {
  position: relative;
  padding: 4% 5% 5% 5%;
  overflow: visible;
}

.collage-grid {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr 1.5fr 1.5fr;
  grid-template-rows: 28vw 28vw;
  gap: 0;
}

/* Every photo/video fills its cell completely */
.collage-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  display: block;
}

.collage-item {
  overflow: hidden;
  transition: transform 0.25s ease;
}

.collage-item:hover {
  z-index: 15;
  transform: scale(1.02);
}

.caption-label {
  z-index: 5;
}

/* Mobile: 2-col auto-flow, stickers hidden, auto row heights */
@media (max-width: 640px) {
  .collage-wrapper { padding: 3% 2%; }

  .collage-grid {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: none;
    grid-auto-rows: 40vw;
  }

  .collage-item {
    grid-column: auto !important;
    grid-row: auto !important;
  }
}
</style>
